//  Leaflet dependencies
import L from 'leaflet';
import 'leaflet.markercluster';

// Custom modules
import { t } from './dictionary.js';
import { renderCard } from './card.js';

// ASSETS
const ICON_BASE_URL = '/themes/sources-energie/energie-eolienne/parcs-eoliens/images/icon';

// MAP CONFIG
const DESKTOP_MIN_WIDTH = 1024;
const DEFAULT_MAP_CENTER = [52, -72];
const DEFAULT_ZOOM = 6;
const MIN_ZOOM = 5;
const MAX_ZOOM = 17;
const MARKER_EDGE_MARGIN = 8;

const desktopBreakpoint = window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`);

// ICON CONFIG
const ICON_WIDTH = 56;
const ICON_HEIGHT = 56;
const CLUSTER_ICON_SIZE = 44;

// MARKER EXTENTS
const MARKER_TOP_EXTENT = Math.max(ICON_HEIGHT, CLUSTER_ICON_SIZE / 2);
const MARKER_SIDE_EXTENT = Math.max(ICON_WIDTH / 2, CLUSTER_ICON_SIZE / 2);
const MARKER_BOTTOM_EXTENT = CLUSTER_ICON_SIZE / 2;

// POPUP CONFIG
const POPUP_MAX_WIDTH = 280;
const POPUP_DESKTOP_GAP = 16;
const POPUP_MOBILE_GAP = 12;
const MOBILE_MARKER_TARGET_Y_RATIO = 0.25;

// CSS CLASSES
const POPUP_POINTER_LEFT_CLASS = 'map-popup-wrapper--pointer-left';

function createIcon(state = '') {
  const suffix = state ? `-${state}` : '';

  return L.icon({
    iconUrl: `${ICON_BASE_URL}/parcs-eoliens${suffix}.svg`,
    iconSize: [ICON_WIDTH, ICON_HEIGHT],
    iconAnchor: [ICON_WIDTH / 2, ICON_HEIGHT],
    popupAnchor: [0, -ICON_HEIGHT],
  });
}

const icons = {
  default: createIcon(),
  active: createIcon('actif'),
};

function createClusterIcon(cluster) {
  return L.divIcon({
    html: `<span>${cluster.getChildCount()}</span>`,
    className: 'map-cluster-icon',
    iconSize: L.point(CLUSTER_ICON_SIZE, CLUSTER_ICON_SIZE),
  });
}

function computePopupOffset(popupEl) {
  const { offsetWidth: width, offsetHeight: height } = popupEl;

  if (desktopBreakpoint.matches) {
    return L.point(
      width / 2 + ICON_WIDTH / 2 + POPUP_DESKTOP_GAP,
      ICON_HEIGHT / 2 + height / 2,
    );
  }

  return L.point(0, ICON_HEIGHT + POPUP_MOBILE_GAP + height);
}

function panMarkerIntoView(map, marker) {
  const size = map.getSize();
  const currentCenter = size.divideBy(2);
  const markerPoint = map.latLngToContainerPoint(marker.getLatLng());
  const targetPoint = desktopBreakpoint.matches
    ? currentCenter
    : L.point(size.x / 2, size.y * MOBILE_MARKER_TARGET_Y_RATIO);

  const newCenterPoint = currentCenter.add(markerPoint.subtract(targetPoint));
  map.panTo(map.containerPointToLatLng(newCenterPoint), { animate: true });
}

function hasValidCoordinates(lng, lat) {
  return Number.isFinite(lng)
    && Number.isFinite(lat)
    && lng >= -180
    && lng <= 180
    && lat >= -90
    && lat <= 90;
}

function renderPopupCloseButton() {
  return `
    <button type="button" class="map-popup__close js-map-popup-close" aria-label="${t('popup.close')}">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
        <path d="M8 9.07612L3.3053 13.7708C3.15288 13.9236 2.97353 14 2.76724 14C2.56133 14 2.38197 13.9236 2.22918 13.7708C2.07639 13.618 2 13.4387 2 13.2328C2 13.0265 2.07639 12.8471 2.22918 12.6947L6.92388 8L2.22918 3.3053C2.07639 3.15288 2 2.97353 2 2.76724C2 2.56133 2.07639 2.38197 2.22918 2.22918C2.38197 2.07639 2.56133 2 2.76724 2C2.97353 2 3.15288 2.07639 3.3053 2.22918L8 6.92388L12.6947 2.22918C12.8471 2.07639 13.0265 2 13.2328 2C13.4387 2 13.618 2.07639 13.7708 2.22918C13.9236 2.38197 14 2.56133 14 2.76724C14 2.97353 13.9236 3.15288 13.7708 3.3053L9.07612 8L13.7708 12.6947C13.9236 12.8471 14 13.0265 14 13.2328C14 13.4387 13.9236 13.618 13.7708 13.7708C13.618 13.9236 13.4387 14 13.2328 14C13.0265 14 12.8471 13.9236 12.6947 13.7708L8 9.07612Z" fill="currentColor"/>
      </svg>
    </button>
  `;
}

function renderPopup(item) {
  return `
    <div class="card">${renderCard({ item, labels: t('popup') })}</div>
    ${renderPopupCloseButton()}
  `;
}

// After fitBounds() the raw lat/lng bounds may leave marker/cluster icons flush against
// the container edges. fitBounds() accepts asymmetric padding, so we reserve extra pixels
// on each side up front — Leaflet then computes the correct center/zoom in a single pass.
const FIT_BOUNDS_PADDING = {
  paddingTopLeft: L.point(
    MARKER_SIDE_EXTENT + MARKER_EDGE_MARGIN,
    MARKER_TOP_EXTENT + MARKER_EDGE_MARGIN,
  ),
  paddingBottomRight: L.point(
    MARKER_SIDE_EXTENT + MARKER_EDGE_MARGIN,
    MARKER_BOTTOM_EXTENT + MARKER_EDGE_MARGIN,
  ),
};

function fitMarkersBounds(map, markers) {
  if (!markers.length) return;

  map.fitBounds(L.featureGroup(markers).getBounds(), FIT_BOUNDS_PADDING);
}

function renderMarkers(map, items) {
  const markers = [];
  const clusterGroup = L.markerClusterGroup({
    iconCreateFunction: createClusterIcon,
    showCoverageOnHover: false,
    spiderfyOnMaxZoom: true,
  });

  let openMarker = null;

  function updatePopupPositioning(popup) {
    const popupEl = popup.getElement();
    if (!popupEl) return;

    popupEl.classList.toggle(POPUP_POINTER_LEFT_CLASS, desktopBreakpoint.matches);
    popup.options.offset = computePopupOffset(popupEl);
    popup.update();
  }

  function repositionOpenPopup() {
    if (!openMarker || !openMarker.isPopupOpen()) return;

    updatePopupPositioning(openMarker.getPopup());
    panMarkerIntoView(map, openMarker);
  }

  function onBreakpointChange() {
    repositionOpenPopup();
  }

  desktopBreakpoint.addEventListener('change', onBreakpointChange);

  items.forEach((item) => {
    const lng = item.longitude;
    const lat = item.latitude;

    if (!hasValidCoordinates(lng, lat)) return;

    const marker = L.marker([lat, lng], {
      icon: icons.default,
      alt: item[t('nameKey')] ?? item['nom-fr'],
    });

    marker.bindPopup(renderPopup(item), {
      closeButton: false,
      maxWidth: POPUP_MAX_WIDTH,
      className: 'map-popup-wrapper',
      autoPan: false,
    });

    marker.on('popupopen', function onPopupOpen(e) {
      marker.setIcon(icons.active);
      openMarker = marker;

      updatePopupPositioning(e.popup);
      panMarkerIntoView(map, marker);

      e.popup.getElement()?.querySelector('.js-map-popup-close')
        ?.addEventListener('click', () => marker.closePopup());
    });

    marker.on('popupclose', function onPopupClose() {
      marker.setIcon(icons.default);

      if (openMarker === marker) {
        openMarker = null;
      }
    });

    clusterGroup.addLayer(marker);
    markers.push(marker);
  });

  clusterGroup.addTo(map);

  function destroy() {
    desktopBreakpoint.removeEventListener('change', onBreakpointChange);
    map.removeLayer(clusterGroup);
  }

  return { markers, destroy };
}

export function renderMap({ el, items }) {
  const map = L.map(el, {
    center: DEFAULT_MAP_CENTER,
    zoom: DEFAULT_ZOOM,
    minZoom: MIN_ZOOM,
    maxZoom: MAX_ZOOM,
    // Allow fractional zoom levels so fitBounds() can tighten the view precisely to the
    // marker edge margins instead of snapping down a whole zoom level (which would make
    // the initial view look far wider than necessary).
    zoomSnap: 0.25,
    zoomDelta: 1,
    maxBoundsViscosity: 1,
    scrollWheelZoom: false,
    zoomControl: false,
  });

  L.control.zoom({ position: 'topright' }).addTo(map);

  // Fond de carte WMTS — Gouvernement du Québec
  //   level = z, row = y, col = x  →  Leaflet : {z}/{y}/{x}
  L.tileLayer(
    'https://geoegl.msp.gouv.qc.ca/carto/wmts/1.0.0/carte_gouv_qc_public/default/EPSG_3857/{z}/{y}/{x}.0',
    {
      attribution: '&copy; Gouvernement du Québec',
      detectRetina: false,
      maxZoom: 17,
    },
  ).addTo(map);

  const { markers, destroy } = renderMarkers(map, items);

  function refreshMapBounds() {
    map.invalidateSize();
    fitMarkersBounds(map, markers);
  }

  requestAnimationFrame(() => {
    map.invalidateSize();
    fitMarkersBounds(map, markers);
  });

  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(() => map.invalidateSize()).observe(el);
  } else {
    window.addEventListener('resize', () => map.invalidateSize());
  }

  function refresh() {
    if (el.offsetWidth === 0) return;
    refreshMapBounds();
  }

  function teardown() {
    destroy();
    map.remove();
  }

  return { refresh, teardown };
}
